/** Launch Kit page (Phase 32) — deterministic, no posting. */
import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  LAUNCH_CHANNELS,
  runLaunchKitDryRun,
  type ApiResult,
  type DryRunChannel,
  type DryRunLanguage,
  type LaunchChannel,
  type LaunchKitRequest,
  type LaunchKitResponse,
  runWorkflowPersistedFirst,
} from "../api/client";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  brand_name: string;
  category: string;
  target_market: string;
  target_language: DryRunLanguage;
  sales_channel: string;
  packaging_positioning: string;
  key_benefit: string;
  claim_restrictions: string;
  qr_connected: boolean;
  launch_channels: string;
  notes: string;
}

const EMPTY: FormState = {
  product_name: "",
  brand_name: "",
  category: "",
  target_market: "",
  target_language: "en",
  sales_channel: "",
  packaging_positioning: "",
  key_benefit: "",
  claim_restrictions: "",
  qr_connected: false,
  launch_channels: "",
  notes: "",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<LaunchKitResponse> };

const splitLines = (s: string, max: number): string[] =>
  s.split("\n").map((x) => x.trim()).filter(Boolean).slice(0, max);

export function LaunchKitPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const r = t.workflowDryRunRequiredHint;
    if (!form.product_name.trim()) errs.product_name = r;
    if (!form.brand_name.trim()) errs.brand_name = r;
    if (!form.category.trim()) errs.category = r;
    if (!form.target_market.trim()) errs.target_market = r;
    if (!form.packaging_positioning.trim()) errs.packaging_positioning = r;
    if (!form.key_benefit.trim()) errs.key_benefit = r;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const body: LaunchKitRequest = {
      product_name: form.product_name.trim(),
      brand_name: form.brand_name.trim(),
      category: form.category.trim(),
      target_market: form.target_market.trim().toUpperCase(),
      target_language: form.target_language,
      sales_channel: form.sales_channel ? (form.sales_channel as DryRunChannel) : null,
      packaging_positioning: form.packaging_positioning.trim(),
      key_benefit: form.key_benefit.trim(),
      claim_restrictions: splitLines(form.claim_restrictions, 16),
      qr_connected: form.qr_connected,
      launch_channels: splitLines(form.launch_channels, 8) as LaunchChannel[],
      notes: form.notes.trim() || null,
      ai_assist: aiAssist,
    };
    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("launch-kit", body, runLaunchKitDryRun);
    setState({ kind: "loaded", result });
  }

  const opt = (vals: readonly string[]) => vals.map((v) => ({ value: v, label: v }));
  const yesNo = [
    { value: "no", label: t.reportReviewRequiredNo },
    { value: "yes", label: t.reportReviewRequiredYes },
  ];

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">{t.appShellNavWorkflows}</span>
        <h1 className="po-section-header__title">{t.launchKitTitle}</h1>
        <p className="po-note">{t.launchKitSubtitle}</p>
      </header>
      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField label={t.intakeFieldProductName} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name} onChange={(v) => update("product_name", v)} error={errors.product_name} maxLength={200} />
            <TextField label={t.intakeFieldBrandName} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.brand_name} onChange={(v) => update("brand_name", v)} error={errors.brand_name} maxLength={200} />
            <TextField label={t.intakeFieldProductCategory} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.category} onChange={(v) => update("category", v)} error={errors.category} maxLength={100} />
            <TextField label={t.labelCopyFieldTargetMarket} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market} onChange={(v) => update("target_market", v.toUpperCase())} error={errors.target_market} maxLength={8} />
            <SelectField label={t.labelCopyFieldTargetLanguage} value={form.target_language}
              onChange={(v) => update("target_language", v as DryRunLanguage)} options={opt(DRY_RUN_LANGUAGES)} />
            <SelectField label={t.labelCopyFieldSalesChannel} value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)} options={opt(DRY_RUN_CHANNELS)} emptyLabel={t.workflowDryRunNotSelected} />
            <SelectField label={t.launchKitFieldQrConnected} value={form.qr_connected ? "yes" : "no"}
              onChange={(v) => update("qr_connected", v === "yes")} options={yesNo} />
            <TextAreaField label={t.launchKitFieldPositioning} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.packaging_positioning} onChange={(v) => update("packaging_positioning", v)} rows={2} maxLength={500} error={errors.packaging_positioning} full />
            <TextAreaField label={t.launchKitFieldKeyBenefit} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.key_benefit} onChange={(v) => update("key_benefit", v)} rows={2} maxLength={500} error={errors.key_benefit} full />
            <TextAreaField label={t.launchKitFieldClaimRestrictions}
              value={form.claim_restrictions} onChange={(v) => update("claim_restrictions", v)} rows={2} full />
            <TextAreaField label={t.launchKitFieldLaunchChannels}
              value={form.launch_channels} onChange={(v) => update("launch_channels", v)} rows={2} full
              hint={`Available: ${LAUNCH_CHANNELS.join(", ")}`} />
            <TextAreaField label={t.intakeFieldNotes} value={form.notes} onChange={(v) => update("notes", v)} rows={2} maxLength={1000} full />
          </FieldGrid>
          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">{t.workflowDryRunValidationFailed}</p>
          )}
          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />
          <div className="po-row">
            <Button variant="primary" type="submit" disabled={state.kind === "submitting"}>
              {state.kind === "submitting" ? t.launchKitSubmitting : t.launchKitSubmit}
            </Button>
            <Button variant="secondary" type="button" onClick={() => { setForm({ ...EMPTY, target_language: form.target_language }); setErrors({}); setState({ kind: "idle" }); }}>
              {t.launchKitReset}
            </Button>
          </div>
        </form>
      </section>
      {state.kind === "loaded" && (
        <DataState state={{ kind: "loaded", result: state.result }} t={t}
          renderOk={(data, requestId) => <LaunchKitReport data={data} requestId={requestId} t={t} />} />
      )}
    </AppLayout>
  );
}

function LaunchKitReport({ data, requestId, t }: { data: LaunchKitResponse; requestId: string | null; t: UiStrings; }): JSX.Element {
  return (
    <div className="po-stack" data-testid="po-launch-kit-report">
      <div className="po-halt-banner po-halt-banner--medium" role="alert">{data.no_send_notice}</div>
      <ReportSection title={t.launchKitReportPositioning}><BulletList items={data.launch_positioning_summary} /></ReportSection>
      <ReportSection title={t.launchKitReportListing}><BulletList items={data.ecommerce_listing_draft} /></ReportSection>
      <ReportSection title={t.launchKitReportSocial}><BulletList items={data.social_content_drafts} /></ReportSection>
      <ReportSection title={t.launchKitReportInsert}><BulletList items={data.insert_card_copy} /></ReportSection>
      <ReportSection title={t.launchKitReportQr}><BulletList items={data.qr_connected_packaging_idea} /></ReportSection>
      <ReportSection title={t.launchKitReportLoyalty}><BulletList items={data.review_reorder_loyalty_copy} /></ReportSection>
      <ReportSection title={t.launchKitReportSafety}><BulletList items={data.claim_safety_notes} /></ReportSection>
      <ReportSection title={t.intakeReportHumanReview}><BulletList items={data.human_review_required_notes} /></ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
